import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { ArrowLeftIcon, BackspaceIcon, LockClosedIcon } from '@heroicons/react/24/outline'

function QuickLogin() {
  const { pinLogin, pinDeviceInfo, isPinDevice } = useAuth()
  const [users, setUsers] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)
  const [pin, setPin] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [shake, setShake] = useState(false)

  const deviceToken = localStorage.getItem('pinDeviceToken') || document.cookie.match(/(?:^|; )pinDeviceToken=([^;]+)/)?.[1]

  const fetchUsers = useCallback(async () => {
    if (!deviceToken) return
    try {
      const res = await fetch('/api/auth/pin/users', {
        headers: { 'X-Device-Token': deviceToken }
      })
      const data = await res.json()
      if (data.success) {
        setUsers(data.users)
      }
    } catch (err) {
      console.error('Failed to fetch users:', err)
    } finally {
      setLoading(false)
    }
  }, [deviceToken])

  useEffect(() => { fetchUsers() }, [fetchUsers])

  // Auto-submit when 4 digits entered
  useEffect(() => {
    if (pin.length === 4 && selectedUser && !submitting) {
      handlePinSubmit(pin)
    }
  }, [pin])

  const handlePinSubmit = async (pinValue) => {
    setSubmitting(true)
    setError(null)

    const result = await pinLogin(selectedUser.userIdentifier, pinValue)

    if (!result.success) {
      setError(result.error)
      setPin('')
      setShake(true)
      setTimeout(() => setShake(false), 500)
    }
    setSubmitting(false)
  }

  const handleDigit = (digit) => {
    if (pin.length < 4 && !submitting) {
      setPin(prev => prev + digit)
      setError(null)
    }
  }

  const handleBackspace = () => {
    setPin(prev => prev.slice(0, -1))
    setError(null)
  }

  const handleBack = () => {
    setSelectedUser(null)
    setPin('')
    setError(null)
  }

  // Keyboard support
  useEffect(() => {
    if (!selectedUser) return

    const handleKeyDown = (e) => {
      if (e.key >= '0' && e.key <= '9') {
        handleDigit(e.key)
      } else if (e.key === 'Backspace') {
        handleBackspace()
      } else if (e.key === 'Escape') {
        handleBack()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedUser, pin, submitting])

  const getInitialColor = (name) => {
    const colors = [
      'bg-red-500', 'bg-orange-500', 'bg-amber-500', 'bg-yellow-500',
      'bg-lime-500', 'bg-green-500', 'bg-emerald-500', 'bg-teal-500',
      'bg-cyan-500', 'bg-sky-500', 'bg-blue-500', 'bg-indigo-500',
      'bg-violet-500', 'bg-purple-500', 'bg-fuchsia-500', 'bg-pink-500'
    ]
    let hash = 0
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash)
    }
    return colors[Math.abs(hash) % colors.length]
  }

  // PIN entry screen
  if (selectedUser) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-sm">
          {/* Back button */}
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            <span>Back</span>
          </button>

          {/* User info */}
          <div className="text-center mb-8">
            <div className={`w-20 h-20 rounded-full ${getInitialColor(selectedUser.name)} flex items-center justify-center text-white text-3xl font-bold mx-auto mb-4`}>
              {selectedUser.name.charAt(0).toUpperCase()}
            </div>
            <h2 className="text-2xl font-bold text-white">{selectedUser.name}</h2>
            <p className="text-slate-400 mt-1">Enter your PIN</p>
          </div>

          {/* PIN dots */}
          <div className={`flex justify-center gap-4 mb-8 ${shake ? 'animate-shake' : ''}`}>
            {[0, 1, 2, 3].map(i => (
              <div
                key={i}
                className={`w-4 h-4 rounded-full transition-all duration-150 ${
                  i < pin.length
                    ? 'bg-amber-500 scale-110'
                    : 'bg-slate-600'
                }`}
              />
            ))}
          </div>

          {/* Error message */}
          {error && (
            <div className="text-center mb-6">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Numeric pad */}
          <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(digit => (
              <button
                key={digit}
                onClick={() => handleDigit(String(digit))}
                disabled={submitting}
                className="w-full aspect-square rounded-2xl bg-slate-800 hover:bg-slate-700 active:bg-slate-600 text-white text-2xl font-semibold transition-colors disabled:opacity-50 border border-slate-700 flex items-center justify-center"
                style={{ minHeight: '72px' }}
              >
                {digit}
              </button>
            ))}
            <div /> {/* Empty space */}
            <button
              onClick={() => handleDigit('0')}
              disabled={submitting}
              className="w-full aspect-square rounded-2xl bg-slate-800 hover:bg-slate-700 active:bg-slate-600 text-white text-2xl font-semibold transition-colors disabled:opacity-50 border border-slate-700 flex items-center justify-center"
              style={{ minHeight: '72px' }}
            >
              0
            </button>
            <button
              onClick={handleBackspace}
              disabled={submitting || pin.length === 0}
              className="w-full aspect-square rounded-2xl bg-slate-800 hover:bg-slate-700 active:bg-slate-600 text-slate-400 transition-colors disabled:opacity-30 border border-slate-700 flex items-center justify-center"
              style={{ minHeight: '72px' }}
            >
              <BackspaceIcon className="w-7 h-7" />
            </button>
          </div>

          {submitting && (
            <div className="text-center mt-6">
              <div className="w-6 h-6 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin mx-auto" />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          {pinDeviceInfo?.deviceName && (
            <p className="text-slate-600 text-xs mb-2">{pinDeviceInfo.deviceName}</p>
          )}
          <a href="/login" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">
            Use Password Login
          </a>
        </div>

        <style>{`
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            20% { transform: translateX(-10px); }
            40% { transform: translateX(10px); }
            60% { transform: translateX(-6px); }
            80% { transform: translateX(6px); }
          }
          .animate-shake { animation: shake 0.4s ease-in-out; }
        `}</style>
      </div>
    )
  }

  // User picker screen
  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/images/scenelight-logo.png" alt="Scenelight" className="w-20 h-20 mx-auto" />
          <h1 className="text-3xl text-white mt-4 tracking-widest" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>SCENELIGHT</h1>
          <p className="text-slate-400 mt-2">Tap your name to sign in</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin mx-auto" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12">
            <LockClosedIcon className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 mb-2">No users have set up a PIN yet.</p>
            <p className="text-slate-500 text-sm">Users can set their PIN in Settings &gt; Profile.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {users.map(user => (
              <button
                key={user.id}
                onClick={() => setSelectedUser(user)}
                className="flex flex-col items-center gap-3 p-4 rounded-xl bg-slate-800 hover:bg-slate-700 active:bg-slate-600 border border-slate-700 hover:border-slate-500 transition-all"
              >
                <div className={`w-14 h-14 rounded-full ${getInitialColor(user.name)} flex items-center justify-center text-white text-xl font-bold`}>
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <span className="text-white text-sm font-medium text-center leading-tight">
                  {user.name}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-12 text-center">
        {pinDeviceInfo?.deviceName && (
          <p className="text-slate-600 text-xs mb-2">{pinDeviceInfo.deviceName}</p>
        )}
        <a href="/login" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">
          Use Password Login
        </a>
      </div>
    </div>
  )
}

export default QuickLogin
